import { Clock, MessageSquare, User } from "lucide-react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import type {
  AppointmentBookingStatus,
  AppointmentListFragment,
  AppointmentOutcome,
} from "@/gql/graphql";
import { useConfig } from "@/hooks/use-config";
import { timezoneUtils } from "@/lib/timezone";
import { ColorUtils } from "@/lib/utils/color-utils";
import { AppointmentOutcomeDropdown } from "./appointment-outcome-dropdown";
import { AppointmentStatusDropdown } from "./appointment-status-dropdown";

interface MobileAppointmentListProps {
  appointments: AppointmentListFragment[];
  currentDate: DateTime;
  onAppointmentClick?: (appointmentId: string) => void;
  onStatusUpdate?: (
    appointmentId: string,
    newBookingStatus: string,
    newOutcome?: string | null
  ) => void;
  onOutcomeUpdate?: (appointmentId: string, newOutcome: string | null) => void;
  onServiceWorkerUpdate?: (
    appointmentId: string,
    serviceId: string,
    workerId: string | null
  ) => void;
}

interface MobileAppointmentCardProps {
  appointment: AppointmentListFragment;
  currentDate: DateTime;
  workerAssignmentEnabled: boolean;
  onAppointmentClick?: (appointmentId: string) => void;
  onStatusUpdate?: (
    appointmentId: string,
    newBookingStatus: string,
    newOutcome?: string | null
  ) => void;
  onOutcomeUpdate?: (appointmentId: string, newOutcome: string | null) => void;
  onServiceWorkerUpdate?: (
    appointmentId: string,
    serviceId: string,
    workerId: string | null
  ) => void;
}

function MobileAppointmentCard({
  appointment,
  currentDate: _currentDate,
  workerAssignmentEnabled,
  onAppointmentClick,
  onStatusUpdate,
  onOutcomeUpdate,
  onServiceWorkerUpdate: _onServiceWorkerUpdate,
}: MobileAppointmentCardProps) {
  const { t } = useTranslation("newCalendar");

  // Use actual appointment data
  const appt = {
    id: appointment.id,
    startTime: timezoneUtils.fromISO(appointment.startTime),
    customer: {
      name:
        `${appointment.customer?.firstName || ""} ${appointment.customer?.lastName || ""}`.trim() ||
        t("common.unknownCustomer"),
    },
    services: appointment.appointmentServices?.map((service) => ({
      name: service.serviceName || t("common.unknownService"),
    })) || [{ name: t("common.unknownService") }],
    worker: {
      name: appointment.appointmentServices?.[0]?.worker?.displayName || "Unassigned",
    },
    status: appointment.bookingStatus || "PENDING",
    outcome: appointment.outcome || null,
  };
  // Clean appointment editing logic with proper timezone handling
  const now = DateTime.now(); // Uses the globally set salon timezone from TimezoneService
  const isPast = appt.startTime < now;
  const isToday = appt.startTime.hasSame(now, "day");

  // Confirmation page logic - prioritize booking status for pending appointments
  // 1. PENDING appointments (any time) -> status editing (need confirmation first)
  // 2. Past CONFIRMED appointments -> outcome editing
  // 3. Today's CONFIRMED appointments -> outcome editing
  // 4. Future CONFIRMED appointments -> status editing (can still be modified)

  const shouldShowOutcome = appt.status === "CONFIRMED" && (isPast || isToday);

  // Status styling
  const _getStatusColor = (status: AppointmentBookingStatus) => {
    switch (status) {
      case "CONFIRMED":
        return "bg-green-100 text-green-800 border-green-200";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "DECLINED":
        return "bg-red-100 text-red-800 border-red-200";
    }
  };

  const _getOutcomeColor = (outcome: AppointmentOutcome) => {
    switch (outcome) {
      case "COMPLETED":
        return "bg-green-100 text-green-800 border-green-200";
      case "NO_SHOW":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Helper function to extract unique workers from appointment
  const getUniqueWorkers = (appointment: AppointmentListFragment) => {
    const workers = new Map();
    const services = appointment.appointmentServices || [];

    services.forEach((service) => {
      if (service.worker) {
        workers.set(service.worker.id, {
          name: service.worker.displayName,
          color: service.worker.color,
        });
      }
    });

    return Array.from(workers.values());
  };

  // Function to get display text based on worker assignment setting
  const getDisplayText = () => {
    if (!workerAssignmentEnabled) {
      // Current logic: show services
      return appt.services.length > 1
        ? `${appt.services.length} services`
        : appt.services[0]?.name || t("common.unknownService");
    }

    // NEW: Show workers when enabled
    const workers = getUniqueWorkers(appointment);
    if (workers.length === 0) {
      // Fallback to service when no workers assigned
      return appt.services[0]?.name || t("common.unknownService");
    }

    return workers.length > 1 ? `${workers.length} workers` : workers[0].name;
  };

  // Function to render worker colors for the appointment
  const renderWorkerColors = (appointment: AppointmentListFragment) => {
    const services = appointment.appointmentServices || [];

    if (services.length === 0) {
      return null;
    }

    // Get unique workers with their colors
    const uniqueWorkers = new Map();
    services.forEach((service) => {
      if (service.worker?.color) {
        uniqueWorkers.set(service.worker.displayName, service.worker.color);
      }
    });

    if (uniqueWorkers.size === 0) {
      return null;
    }

    // If multiple workers, remove dot indicators entirely
    if (uniqueWorkers.size > 1) {
      return null;
    }

    // Single unique worker: show one color dot
    const [single] = Array.from(uniqueWorkers.entries());
    if (!single) return null;
    const [name, color] = single;
    const colorStyle = ColorUtils.getColorStyle(color);
    return (
      <div
        className={`w-2 h-2 rounded-full ${colorStyle.className}`}
        style={colorStyle.style}
        title={name}
      />
    );
  };

  return (
    <Card
      className="mb-3 cursor-pointer hover:bg-muted/30 transition-colors"
      onClick={() => onAppointmentClick?.(appt.id)}
    >
      <CardContent className="p-4">
        {/* Top Row: Time (left) + Service (right) */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{appt.startTime.toFormat("HH:mm")}</span>
            {appointment.customerMessage && (
              <MessageSquare className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
            )}
          </div>

          {/* Service/Worker in top right */}
          <div className="text-right flex-shrink-0">
            <div className="flex items-center justify-end gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap truncate max-w-[150px]">
                {getDisplayText()}
              </span>
              {renderWorkerColors(appointment)}
            </div>
          </div>
        </div>

        {/* Bottom Row: Customer (left) + Status/Outcome (right) */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="text-sm truncate">{appt.customer.name}</span>
              {appointment.customer?.totalVisits === 0 && (
                <Badge
                  variant="secondary"
                  className="text-xs px-1.5 py-0 h-5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 whitespace-nowrap flex-shrink-0"
                >
                  {t("common.newCustomer")}
                </Badge>
              )}
            </div>
          </div>

          {/* Status/Outcome in bottom right */}
          <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
            {/* Clean conditional logic */}
            {shouldShowOutcome ? (
              <AppointmentOutcomeDropdown
                appointmentId={appt.id}
                currentOutcome={appt.outcome}
                onOutcomeChange={(newOutcome) => onOutcomeUpdate?.(appt.id, newOutcome)}
              />
            ) : (
              <AppointmentStatusDropdown
                appointmentId={appt.id}
                currentStatus={appt.status}
                onStatusChange={(newStatus) => onStatusUpdate?.(appt.id, newStatus)}
              />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function MobileAppointmentList({
  appointments,
  currentDate,
  onAppointmentClick,
  onStatusUpdate,
  onOutcomeUpdate,
  onServiceWorkerUpdate,
}: MobileAppointmentListProps) {
  // Check if worker assignment is enabled
  const { value: workerAssignmentEnabled = false } = useConfig(
    "salon.appointment_worker_assignment_enabled"
  );

  return (
    <div className="space-y-0">
      {appointments.map((appointment) => (
        <MobileAppointmentCard
          key={appointment.id}
          appointment={appointment}
          currentDate={currentDate}
          workerAssignmentEnabled={workerAssignmentEnabled}
          onAppointmentClick={onAppointmentClick}
          onStatusUpdate={onStatusUpdate}
          onOutcomeUpdate={onOutcomeUpdate}
          onServiceWorkerUpdate={onServiceWorkerUpdate}
        />
      ))}
    </div>
  );
}
